import { Calendar, BookOpen, Target, AlertCircle, CheckCircle, PieChart } from "lucide-react";
import { useMemo } from "react";

const ValidationImportSequence = ({ sequencesWithDates, selectedAttendus, ThemeDetail }) => {

  // Calculs des statistiques
  const stats = useMemo(() => {
    // Séquences par période
    const sequencesByPeriod = sequencesWithDates.reduce((acc, seq) => {
      const period = seq.periode || 'Non définie';
      acc[period] = (acc[period] || 0) + 1;
      return acc;
    }, {});

    // Séquences par thème
    const sequencesByTheme = sequencesWithDates.reduce((acc, seq) => {
      const theme = seq.theme || 'Non défini';
      acc[theme] = (acc[theme] || 0) + 1;
      return acc;
    }, {});

    // Attendus sélectionnés au total
    const totalSelectedAttendus = Object.values(selectedAttendus).flat().length;

    // Tous les attendus disponibles — OBJECTIFS seuls : les automatismes ne
    // sont pas sélectionnables pour une séquence (décision 2026-08-18), les
    // compter fausserait le taux de couverture.
    const allAvailableAttendus = [];
    ThemeDetail?.forEach(theme => {
      theme.descripteur?.forEach(desc => {
        desc.attendus?.forEach(att => {
          if (att.rubrique !== 'automatisme') allAvailableAttendus.push(att.id);
        });
      });
    });

    // IDs des attendus sélectionnés
    const selectedAttenduIds = Object.values(selectedAttendus)
      .flat()
      .map(att => att.id);

    // Attendus non couverts
    const uncoveredAttendus = allAvailableAttendus.filter(
      id => !selectedAttenduIds.includes(id)
    );

    // Dates de début et fin
    const startDate = sequencesWithDates[0]?.dateDebut;
    const endDate = sequencesWithDates[sequencesWithDates.length - 1]?.dateFin;

    return {
      sequencesByPeriod,
      sequencesByTheme,
      totalSequences: sequencesWithDates.length,
      totalSelectedAttendus,
      totalAvailableAttendus: allAvailableAttendus.length,
      uncoveredAttendusCount: uncoveredAttendus.length,
      coveragePercentage: allAvailableAttendus.length > 0
        ? Math.round((selectedAttenduIds.length / allAvailableAttendus.length) * 100)
        : 0,
      startDate,
      endDate
    };
  }, [sequencesWithDates, selectedAttendus, ThemeDetail]);

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 xl:p-3 2xl:p-4 3xl:p-6">
      <div className="flex items-center justify-between mb-6 xl:mb-3 2xl:mb-4 3xl:mb-6">
        <h3 className="text-2xl xl:text-base 2xl:text-lg 3xl:text-2xl font-bold text-gray-800 flex items-center gap-3">
          <CheckCircle className="w-7 h-7 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-green-600" />
          Résumé de votre progression
        </h3>
      </div>

      {/* Vue d'ensemble */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 xl:gap-2 2xl:gap-3 3xl:gap-4 mb-6 xl:mb-3 2xl:mb-4 3xl:mb-6">
        <div className="bg-white rounded-xl p-4 xl:p-2 2xl:p-3 3xl:p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-600">Période couverte</p>
              <p className="text-lg xl:text-xs 2xl:text-sm 3xl:text-lg font-bold text-gray-800">
                {stats.startDate && stats.endDate ? (
                  <>
                    {new Date(stats.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' → '}
                    {new Date(stats.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </>
                ) : 'Non définie'}
              </p>
            </div>
            <Calendar className="w-10 h-10 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-blue-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 xl:p-2 2xl:p-3 3xl:p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-600">Total séquences</p>
              <p className="text-2xl xl:text-base 2xl:text-lg 3xl:text-2xl font-bold text-gray-800">{stats.totalSequences}</p>
            </div>
            <BookOpen className="w-10 h-10 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-purple-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 xl:p-2 2xl:p-3 3xl:p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-600">Attendus couverts</p>
              <p className="text-2xl xl:text-base 2xl:text-lg 3xl:text-2xl font-bold text-gray-800">{stats.coveragePercentage}%</p>
            </div>
            <Target className="w-10 h-10 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-green-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Détails par période */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-3 2xl:gap-4 3xl:gap-6">

        {/* Séquences par période */}
        <div className="bg-white rounded-xl p-4 xl:p-2 2xl:p-3 3xl:p-4 shadow-sm">
          <h4 className="font-semibold text-gray-800 mb-3 xl:mb-2 2xl:mb-2 3xl:mb-3 flex items-center gap-2 xl:text-xs 2xl:text-sm 3xl:text-base">
            <PieChart className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-blue-600" />
            Répartition par période
          </h4>
          <div className="space-y-2">
            {Object.entries(stats.sequencesByPeriod).map(([period, count]) => (
              <div key={period} className="flex justify-between items-center">
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-600">Période {period}</span>
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {count} séquence{count > 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Séquences par thème */}
        <div className="bg-white rounded-xl p-4 xl:p-2 2xl:p-3 3xl:p-4 shadow-sm">
          <h4 className="font-semibold text-gray-800 mb-3 xl:mb-2 2xl:mb-2 3xl:mb-3 flex items-center gap-2 xl:text-xs 2xl:text-sm 3xl:text-base">
            <BookOpen className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-purple-600" />
            Répartition par thème
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {Object.entries(stats.sequencesByTheme).map(([theme, count]) => (
              <div key={theme} className="flex justify-between items-center">
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-600 truncate max-w-[200px]" title={theme}>
                  {theme}
                </span>
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                  {count>1 ? count +  " séquences" : count + " séquence"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alertes */}
      <div className="mt-6 xl:mt-3 2xl:mt-4 3xl:mt-6 space-y-3">
        {/* Attendus sélectionnés */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 xl:p-2 2xl:p-3 3xl:p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 xl:w-4 xl:h-4 3xl:w-5 3xl:h-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-semibold text-green-800 xl:text-xs 2xl:text-sm 3xl:text-base">Attendus sélectionnés</p>
              <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-green-600 mt-1">
                {stats.totalSelectedAttendus} attendus sélectionnés sur {stats.totalAvailableAttendus} disponibles
              </p>
            </div>
          </div>
        </div>

        {/* Alerte attendus non couverts */}
        {stats.uncoveredAttendusCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 xl:p-2 2xl:p-3 3xl:p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 xl:w-4 xl:h-4 3xl:w-5 3xl:h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800 xl:text-xs 2xl:text-sm 3xl:text-base">Attendus non couverts</p>
                <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-amber-600 mt-1">
                  {stats.uncoveredAttendusCount} attendus n'ont été sélectionnés dans aucune séquence.
                  Vous pourrez les ajouter ultérieurement si nécessaire.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ValidationImportSequence;
